import { useMemo, useState, type SVGProps } from "react";
import styles from "./SettingsProtestHistoryPage.module.css";
import { Modal } from "@/shared/modal";
import { useSettingsProtestHistoryViewModel } from "@/features/settings/viewModal/useSettingsProtestHistoryViewModel";
import type { ProtestsExemptionsPayment } from "@/features/settings/services/protestsExemptions.service";
import protestHistoryHeroHouse from "@/assets/images/protest-history-hero-house.jpg";
import fallbackHouse1 from "@/assets/images/prop-fallback-1.jpg";
import fallbackHouse2 from "@/assets/images/prop-fallback-2.jpg";
import { usePropertyStreetViews, type PropertyLike } from "@/shared/utils";
import { UpdateExemptionDocumentsModal } from "./UpdateExemptionDocumentsModal";

type StatusTheme = "approved" | "filed" | "inReview" | "rejected";
type StepVisualState = "completed" | "active" | "pending";

const stepperNodeClassMap: Record<StepVisualState, string> = {
  completed: "stepperNodeCompleted",
  active: "stepperNodeActive",
  pending: "stepperNodePending",
};

const normalizeStatus = (value?: string | null): string => {
  return (value || "").toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
};

const getStatusTheme = (record: ProtestsExemptionsPayment): StatusTheme => {
  const status = normalizeStatus(record.protest_status || record.homestead_status);

  if (status === "approved") {
    return "approved";
  }
  if (status === "rejected" || status === "reject") {
    return "rejected";
  }
  if (status === "inprogress" || status === "inreview") {
    return "inReview";
  }
  return "filed";
};

const getStepVisualState = (
  index: number,
  steps: Array<{ done: boolean }>,
  theme: StatusTheme
): StepVisualState => {
  const step = steps[index];
  if (!step) {
    return "pending";
  }
  if (theme === "approved" || theme === "rejected") {
    return step.done ? "completed" : "pending";
  }
  if (theme === "inReview") {
    if (index === 0) {
      return "completed";
    }
    if (index === 1) {
      return "active";
    }
    return "pending";
  }
  if (index === 0 && step.done) {
    return "completed";
  }
  if (index === 1) {
    return "active";
  }
  return "pending";
};

const DocumentIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M8 3h6l4 4v14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TaxYearIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M7 10h4M13 10h4M7 14h10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const EyeIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M2.5 12C4.5 7.5 8 5 12 5s7.5 2.5 9.5 7c-2 4.5-5.5 7-9.5 7s-7.5-2.5-9.5-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const CheckIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M6 12.5 10 16.5 18 8.5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 8v4.5l2.5 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const StatusBadgeIcon = ({ theme }: { theme: StatusTheme }) => {
  if (theme === "approved") {
    return <CheckIcon className={styles.statusBadgeIcon} aria-hidden="true" />;
  }
  if (theme === "inReview") {
    return <ClockIcon className={styles.statusBadgeIcon} aria-hidden="true" />;
  }
  return <DocumentIcon className={styles.statusBadgeIcon} aria-hidden="true" />;
};

export const SettingsProtestHistoryPage = () => {
  const {
    activeTab,
    setActiveTab,
    page,
    setPage,
    pageSize,
    setPageSize,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    records,
    protestCount,
    exemptionCount,
    stats,
    totalPages,
    totalRecords,
    isLoading,
    isError,
    previewRecord,
    setPreviewRecord,
    activePreviewKey,
    setActivePreviewKey,
    previewDetails,
    currentPreviewUrl,
    helpers,
  } = useSettingsProtestHistoryViewModel();

  const [updateDocsRecord, setUpdateDocsRecord] = useState<ProtestsExemptionsPayment | null>(null);

  const propertyItems = useMemo((): PropertyLike[] => {
    return records.map((record) => {
      const details = record.property_details;
      const addr =
        details?.full_address ||
        [details?.city, details?.state, details?.zip].filter(Boolean).join(", ") ||
        "";
      return {
        property_id: record.propertyId || String(record.id),
        address: addr,
      };
    });
  }, [records]);

  const streetViewImages = usePropertyStreetViews(propertyItems);

  return (
    <div className={styles.page}>
      {/* HEADER SECTION */}
      <div className={styles.headerSection}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Protest History</h1>
          <p className={styles.pageDescription}>
            All protests you&apos;ve filed for your properties
          </p>

          <div className={styles.tabList} role="tablist" aria-label="Protest history tabs">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "protest"}
              className={`${styles.tab} ${activeTab === "protest" ? styles.tabActive : ""}`.trim()}
              onClick={() => setActiveTab("protest")}
            >
              Protest ({protestCount})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "exemption"}
              className={`${styles.tab} ${activeTab === "exemption" ? styles.tabActive : ""}`.trim()}
              onClick={() => setActiveTab("exemption")}
            >
              Exemption ({exemptionCount})
            </button>
          </div>
        </div>

        <div className={styles.headerBannerWrap}>
          <img
            src={protestHistoryHeroHouse}
            alt="Protest History"
            className={styles.headerBannerImg}
          />
        </div>
      </div>

      {/* 4 STAT METRICS CARDS */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconBlue}`}>
            <DocumentIcon className={styles.statIcon} aria-hidden="true" />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Total Protests</span>
            <span className={styles.statValue}>{stats.total}</span>
            <span className={`${styles.statSub} ${styles.statSubGreen}`}>
              ↑ {stats.thisYear} this year
            </span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconGreen}`}>
            <CheckIcon className={styles.statIcon} aria-hidden="true" />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Approved</span>
            <span className={styles.statValue}>{stats.approved}</span>
            <span className={`${styles.statSub} ${styles.statSubGreen}`}>
              {stats.approvalRate}% approval rate
            </span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconCyan}`}>
            <ClockIcon className={styles.statIcon} aria-hidden="true" />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Under Review</span>
            <span className={styles.statValue}>{stats.underReview}</span>
            <span className={`${styles.statSub} ${styles.statSubBlue}`}>
              {stats.inProgressRate}% in progress
            </span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconPurple}`}>
            <DocumentIcon className={styles.statIcon} aria-hidden="true" />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Not Filed</span>
            <span className={styles.statValue}>{stats.notFiled}</span>
            <button
              type="button"
              className={`${styles.statSub} ${styles.statSubPurple}`}
              onClick={() => {
                setStatusFilter("not_filed");
                setPage(1);
              }}
            >
              Get started →
            </button>
          </div>
        </div>
      </div>

      {/* SEARCH & FILTERS ROW */}
      <div className={styles.filtersRow}>
        <div className={styles.searchBox}>
          <svg
            className={styles.searchIcon}
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by address, city or protest ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            Status
            <select
              className={styles.filterSelect}
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="not_filed">Not Filed</option>
              <option value="filed">Submitted</option>
              <option value="inprogress">In Progress</option>
              <option value="approved">Approved</option>
              <option value="reject">Rejected</option>
            </select>
          </label>

          <label className={styles.filterLabel}>
            Show
            <select
              className={styles.filterSelect}
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </label>
        </div>
      </div>

      {/* CARDS LIST */}
      <div className={styles.cardList}>
        {isLoading && <article className={styles.historyCard}>Loading records...</article>}
        {!isLoading && isError && (
          <article className={styles.historyCard}>Unable to load records. Please refresh.</article>
        )}
        {!isLoading && !isError && records.length === 0 && (
          <article className={styles.historyCard}>No records found.</article>
        )}
        {records.map((record, index) => {
          const statusTheme = getStatusTheme(record);
          const timelineSteps = helpers.getTimelineSteps(record);
          const propId = record.propertyId || String(record.id);
          const streetViewUrl = streetViewImages[propId];
          const fallbackImg = index % 2 === 0 ? fallbackHouse1 : fallbackHouse2;

          return (
            <article className={styles.historyCard} key={record.id} data-status={statusTheme}>
              <div className={styles.propertyThumbWrap}>
                <img
                  src={streetViewUrl || fallbackImg}
                  alt={helpers.toAddress(record)}
                  className={styles.propertyThumbImg}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = fallbackImg;
                  }}
                />
              </div>

              <div className={styles.cardMain}>
                <h2 className={styles.propertyAddress}>{helpers.toAddress(record)}</h2>

                <p className={styles.meta}>
                  {helpers.formatPropertyType(record.property_details?.property_type)} &#8226; 1 Day
                  Found
                </p>

                <div className={styles.detailList}>
                  <p className={styles.detail}>
                    <DocumentIcon className={styles.detailIcon} aria-hidden="true" />
                    <span>Protest ID: {record.id}</span>
                  </p>
                  <p className={styles.detail}>
                    <CalendarIcon className={styles.detailIcon} aria-hidden="true" />
                    <span>Filed on: {helpers.formatDate(record.created_at)}</span>
                  </p>
                  <p className={styles.detail}>
                    <TaxYearIcon className={styles.detailIcon} aria-hidden="true" />
                    <span>Tax Year: {record.year ?? "2027"}</span>
                  </p>
                </div>

                <div className={styles.stepper}>
                  {timelineSteps.map((step, sIdx, list) => {
                    const visualState = getStepVisualState(sIdx, list, statusTheme);
                    const connectorDone =
                      sIdx > 0 && getStepVisualState(sIdx - 1, list, statusTheme) === "completed";

                    return (
                      <div key={step.key} className={styles.stepperStep}>
                        <div className={styles.stepperTrack}>
                          {sIdx > 0 && (
                            <div
                              className={`${styles.stepperLine} ${connectorDone ? styles.stepperLineDone : ""}`.trim()}
                              aria-hidden="true"
                            />
                          )}
                          <div
                            className={`${styles.stepperNode} ${styles[stepperNodeClassMap[visualState]]}`.trim()}
                            aria-hidden="true"
                          />
                          {sIdx < list.length - 1 && (
                            <div
                              className={`${styles.stepperLine} ${visualState === "completed" ? styles.stepperLineDone : ""}`.trim()}
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <span className={styles.stepperLabel}>{step.label}</span>
                        <span className={styles.stepperDate}>{step.date}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.cardRightCol}>
                <span className={`${styles.statusBadge} ${styles[`statusBadge_${statusTheme}`]}`}>
                  <StatusBadgeIcon theme={statusTheme} />
                  {helpers.toTitleCase(record.protest_status || record.homestead_status || "filed")}
                </span>

                <div className={styles.cardActionsRow}>
                  {activeTab === "exemption" && (
                    <button
                      type="button"
                      className={styles.updateExemptionDocsBtn}
                      onClick={() => setUpdateDocsRecord(record)}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Update Docs</span>
                    </button>
                  )}

                  <button
                    type="button"
                    className={styles.viewDetailsButton}
                    onClick={() => {
                      const details = helpers.collectDocumentDetails(record);
                      setPreviewRecord(record);
                      setActivePreviewKey(details[0]?.key ?? null);
                    }}
                  >
                    <EyeIcon className={styles.viewDetailsIcon} aria-hidden="true" />
                    <span>View</span>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {!isLoading && !isError && totalPages > 0 && (
        <div className={styles.paginationRow}>
          <button
            type="button"
            className={styles.paginationButton}
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span className={styles.paginationInfo}>
            Page {page} of {totalPages} ({totalRecords} records)
          </span>
          <button
            type="button"
            className={styles.paginationButton}
            disabled={page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </button>
        </div>
      )}

      {/* DOCUMENT PREVIEW MODAL */}
      {previewRecord && (
        <Modal
          isOpen
          onClose={() => {
            setPreviewRecord(null);
            setActivePreviewKey(null);
          }}
          unconstrained
          className={styles.modalCard}
          headerClassName={styles.modalHeader}
          title={
            <div className={styles.modalHeaderTitleWrap}>
              <div className={styles.modalDocBadge}>
                <DocumentIcon className={styles.modalDocBadgeIcon} aria-hidden="true" />
              </div>
              <div className={styles.modalHeaderInfo}>
                <span className={styles.modalEyebrow}>DOCUMENTS</span>
                <h2 className={styles.modalAddress}>{helpers.toAddress(previewRecord)}</h2>
              </div>
            </div>
          }
          titleClassName={styles.modalTitleWrap}
          bodyClassName={styles.modalBody}
          ariaLabel={`Documents for ${helpers.toAddress(previewRecord)}`}
        >
          <div className={styles.docList}>
            {(activeTab === "exemption" || previewRecord.type === "homestead") && (
              <div style={{ paddingBottom: "10px" }}>
                <button
                  type="button"
                  className={styles.updateExemptionDocsBtn}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => setUpdateDocsRecord(previewRecord)}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>Upload / Replace Exemption Documents</span>
                </button>
              </div>
            )}
            {previewDetails.length === 0 && (
              <p className={styles.noDocs}>No documents found for this record.</p>
            )}
            {previewDetails.map((doc) => {
              const isActive = activePreviewKey === doc.key;
              const cleanLabel = doc.label.replace(/\s*#\d+$/, "");
              const isPdf = helpers.isPdfUrl(doc.url);

              return (
                <div
                  key={doc.key}
                  className={`${styles.docCard} ${isActive ? styles.docCardActive : ""}`.trim()}
                  onClick={() => setActivePreviewKey(doc.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setActivePreviewKey(doc.key);
                    }
                  }}
                >
                  <div className={styles.docCardLeft}>
                    <div
                      className={`${styles.docIconWrap} ${isActive ? styles.docIconWrapActive : ""}`}
                    >
                      <DocumentIcon className={styles.docItemIcon} aria-hidden="true" />
                    </div>
                    <div className={styles.docInfo}>
                      <span className={styles.docTitle}>{cleanLabel}</span>
                      <span className={styles.docMeta}>
                        {isPdf ? "PDF" : "Image"} &#8226; Document
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`${styles.docViewAction} ${isActive ? styles.docViewActionActive : ""}`.trim()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePreviewKey(doc.key);
                    }}
                  >
                    <EyeIcon className={styles.docViewEye} aria-hidden="true" />
                    <span>View</span>
                  </button>
                </div>
              );
            })}
          </div>

          <div className={styles.previewPane}>
            {!currentPreviewUrl && <p className={styles.noDocs}>Select a document to preview.</p>}
            {currentPreviewUrl && helpers.isImageUrl(currentPreviewUrl) && (
              <div className={styles.previewImageContainer}>
                <img
                  src={currentPreviewUrl}
                  alt="Document preview"
                  className={styles.previewImage}
                />
              </div>
            )}
            {currentPreviewUrl && helpers.isPdfUrl(currentPreviewUrl) && (
              <iframe
                src={`${currentPreviewUrl}#toolbar=1&navpanes=1`}
                title="PDF Document Preview"
                className={styles.previewFrame}
              />
            )}
            {currentPreviewUrl &&
              !helpers.isImageUrl(currentPreviewUrl) &&
              !helpers.isPdfUrl(currentPreviewUrl) && (
                <div className={styles.previewFallback}>
                  <p className={styles.noDocs}>Preview not available for this file type.</p>
                  <a
                    href={currentPreviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.openExternal}
                  >
                    Open document in new tab
                  </a>
                </div>
              )}
          </div>
        </Modal>
      )}

      <UpdateExemptionDocumentsModal
        isOpen={Boolean(updateDocsRecord)}
        onClose={() => setUpdateDocsRecord(null)}
        record={updateDocsRecord}
      />
    </div>
  );
};
